import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import { submissionsAPI } from '../services/api';

type Period = 'week' | 'month' | '3months' | '6months' | 'year';

interface ManagerQueueStats {
  manager_id: number;
  manager_name: string;
  manager_email: string;
  website_queue_count: number;
  third_party_queue_count: number;
}

interface QueueStatsResponse {
  period: string;
  start_date: string;
  end_date: string;
  queue_split_date: string;
  manager_stats: ManagerQueueStats[];
  totals: {
    website_queue_count: number;
    third_party_queue_count: number;
  };
}

const PERIOD_LABELS: Record<Period, string> = {
  week: 'Last Week',
  month: 'Last Month',
  '3months': 'Last 3 Months',
  '6months': 'Last 6 Months',
  year: 'Last Year',
};

// Fallback used only until the API response (queue_split_date) arrives.
const FALLBACK_QUEUE_SPLIT_DATE = '2026-09-28';

// Fixed anchor date for the "Since Jul 11, 2026" quick filter — always runs through today.
const ANCHOR_START_DATE = '2026-07-11';
const ANCHOR_BUTTON_LABEL = 'Since Jul 11, 2026';

type FilterMode = 'period' | 'custom';

const QueueStatsPage: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<Record<Period, QueueStatsResponse | null>>({} as Record<Period, QueueStatsResponse | null>);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('week');

  const [filterMode, setFilterMode] = useState<FilterMode>('period');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [customStats, setCustomStats] = useState<QueueStatsResponse | null>(null);
  const [customLoading, setCustomLoading] = useState(false);
  const [customError, setCustomError] = useState<string | null>(null);

  const loadStats = useCallback(async (period: Period) => {
    try {
      setLoading(true);
      const data = await submissionsAPI.getQueueStats({ period });
      setStats(prev => ({ ...prev, [period]: data }));
    } catch (err: any) {
      console.error('Failed to load queue stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCustomRange = useCallback(async (start: string, end: string) => {
    try {
      setCustomLoading(true);
      setCustomError(null);
      const data = await submissionsAPI.getQueueStats({ startDate: start, endDate: end });
      setCustomStats(data);
    } catch (err: any) {
      setCustomError(err?.response?.data?.error || 'Failed to load queue stats for this date range.');
      setCustomStats(null);
    } finally {
      setCustomLoading(false);
    }
  }, []);

  const loadCustomStats = useCallback(() => {
    if (!customStart || !customEnd) {
      setCustomError('Please choose both a start and end date.');
      return;
    }
    fetchCustomRange(customStart, customEnd);
  }, [customStart, customEnd, fetchCustomRange]);

  const loadSinceAnchor = useCallback(() => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setFilterMode('custom');
    setCustomStart(ANCHOR_START_DATE);
    setCustomEnd(todayStr);
    fetchCustomRange(ANCHOR_START_DATE, todayStr);
  }, [fetchCustomRange]);

  const initialLoadDone = React.useRef(false);
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      loadStats(selectedPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filterMode === 'period' && !stats[selectedPeriod]) {
      loadStats(selectedPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, filterMode]);

  // Check if user has access
  if (user && !user.can_view_all_tasks) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
              <h1 className="text-2xl font-bold text-red-900 mb-4">⛔ Access Denied</h1>
              <p className="text-red-800">Only Owners and Administrators can view queue statistics.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const currentStats = filterMode === 'custom' ? customStats : stats[selectedPeriod];
  const isLoading = filterMode === 'period' && loading && !currentStats;
  const isAnchorActive = filterMode === 'custom' && customStart === ANCHOR_START_DATE;

  if (isLoading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
            <p className="mt-4 text-gray-600">Loading statistics...</p>
          </div>
        </div>
      </>
    );
  }

  const managerRows = [...(currentStats?.manager_stats || [])].sort(
    (a, b) => (b.website_queue_count + b.third_party_queue_count) - (a.website_queue_count + a.third_party_queue_count)
  );
  const totalWebsite = currentStats?.totals.website_queue_count || 0;
  const totalThirdParty = currentStats?.totals.third_party_queue_count || 0;

  const queueSplitDate = currentStats?.queue_split_date || FALLBACK_QUEUE_SPLIT_DATE;
  const rangeIncludesPreSplit = !!currentStats && currentStats.start_date.slice(0, 10) < queueSplitDate;

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">🔀 Queue Distribution</h1>
            <p className="mt-2 text-gray-600">
              Lead distribution per manager, split by Website vs Third-party queues — use this to verify
              both queues rotate evenly.
            </p>
          </div>

          {/* Period Selector */}
          <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <button
                onClick={() => setFilterMode('period')}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
                  filterMode === 'period'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Preset Periods
              </button>
              <button
                onClick={() => setFilterMode('custom')}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
                  filterMode === 'custom'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Custom Date Range
              </button>

              <span className="w-px h-6 bg-gray-200 mx-1" />

              <button
                onClick={loadSinceAnchor}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
                  isAnchorActive
                    ? 'bg-accent-500 text-white shadow'
                    : 'bg-accent-50 text-accent-700 hover:bg-accent-100'
                }`}
              >
                {ANCHOR_BUTTON_LABEL}
              </button>
            </div>

            {filterMode === 'period' ? (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Time Period:
                </label>
                <div className="flex flex-wrap gap-2">
                  {(Object.keys(PERIOD_LABELS) as Period[]).map((period) => (
                    <button
                      key={period}
                      onClick={() => {
                        setSelectedPeriod(period);
                        if (!stats[period]) {
                          loadStats(period);
                        }
                      }}
                      className={`px-6 py-3 rounded-lg font-semibold transition ${
                        selectedPeriod === period
                          ? 'bg-accent-500 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {PERIOD_LABELS[period]}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Date Range:
                </label>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">From</label>
                    <input
                      type="date"
                      value={customStart}
                      max={customEnd || undefined}
                      onChange={(e) => setCustomStart(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">To</label>
                    <input
                      type="date"
                      value={customEnd}
                      min={customStart || undefined}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500"
                    />
                  </div>
                  <button
                    onClick={loadCustomStats}
                    disabled={customLoading || !customStart || !customEnd}
                    className="px-6 py-2 rounded-lg font-semibold bg-accent-500 text-white shadow hover:bg-accent-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {customLoading ? 'Loading…' : 'Apply'}
                  </button>
                </div>
                {customError && (
                  <p className="mt-3 text-sm text-red-600">{customError}</p>
                )}
              </div>
            )}
          </div>

          {/* Queue split notice */}
          {rangeIncludesPreSplit && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              <strong>Note:</strong> Website and Third-party queues were separated on{' '}
              {new Date(`${queueSplitDate}T00:00:00`).toLocaleDateString(undefined, {
                year: 'numeric', month: 'long', day: 'numeric',
              })}. Selected dates before that reflect leads that hadn't been divided into
              separate queues yet, so the Website / Third-party split for that period doesn't represent a
              real distribution.
            </div>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-primary-100 text-sm font-medium">Website Queue</p>
                  <p className="text-3xl font-bold mt-2">{totalWebsite}</p>
                </div>
                <div className="bg-primary-400 bg-opacity-30 rounded-full p-3">
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 100-8 4 4 0 000 8zm6 3c0 1.657-3.134 3-7 3s-7-1.343-7-3" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium">Third-party Queue</p>
                  <p className="text-3xl font-bold mt-2">{totalThirdParty}</p>
                </div>
                <div className="bg-purple-400 bg-opacity-30 rounded-full p-3">
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-9-9c2.52 0 4.93 1 6.74 2.74L21 8M21 3v5h-5" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Managers List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Leads by Manager and Queue</h2>
              <p className="text-sm text-gray-600 mt-1">
                {filterMode === 'custom'
                  ? (currentStats ? `Showing data from ${customStart} to ${customEnd}` : 'Choose a date range and click Apply')
                  : `Showing data for ${PERIOD_LABELS[selectedPeriod].toLowerCase()}`}
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Manager
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Website
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Third-party
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {managerRows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                        No leads assigned in this period
                      </td>
                    </tr>
                  ) : (
                    managerRows.map((manager) => (
                      <tr key={manager.manager_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-gray-900">{manager.manager_name}</p>
                          <p className="text-xs text-gray-500">{manager.manager_email}</p>
                        </td>
                        <td className="px-6 py-4 text-center text-sm font-semibold text-primary-700">
                          {manager.website_queue_count}
                        </td>
                        <td className="px-6 py-4 text-center text-sm font-semibold text-purple-700">
                          {manager.third_party_queue_count}
                        </td>
                        <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                          {manager.website_queue_count + manager.third_party_queue_count}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QueueStatsPage;
